import React from 'react'
import { AxiosError } from 'axios'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormControl } from '../../../../components/common/FormControl'
import { Modal } from '../../../../components/common/Modal'
import { ModalBody } from '../../../../components/common/Modal/ModalBody'
import { ModalHeader } from '../../../../components/common/Modal/ModalHeader'
import { SelectDropdown } from '../../../../components/common/SelectDropdown'
import { TextInput } from '../../../../components/common/TextInput'
import { Button, ButtonVariant } from '../../../../components/common/Button'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import {
  ManagementPartner,
  getPartnerNameId,
  getPartnerId,
} from '../../../../components/society/ManagementPartner/management_partner'
import { adminDiscountSchema } from '../../../../forms/schemas/admin_discount'
import { theme } from '../../../../theme/theme'
import {
  adminQueries,
  patchDiscountCode as patchDiscountCodeRequest,
  removeDiscountCode as removeDiscountCodeRequest,
} from '../../../../api/admin'
import { NAdmin } from '../../../../interfaces/services/admin.interfaces'

interface UpdateDiscountModalProps {
  show: boolean
  close: () => void
  discount: NAdmin.NGetDiscount.IResponseInternal[number]
}

export const UpdateDiscountModal = ({
  show,
  close,
  discount,
}: UpdateDiscountModalProps): React.ReactNode => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false)
  const queryClient = useQueryClient()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const defaultDiscountCodeValues = React.useMemo(
    () => ({
      code: discount.code,
      discount: discount.discount,
      partnerNameId:
        getPartnerNameId(discount.partnerId) ??
        ManagementPartner.BOSTADSRATTERNA,
      articleNo: discount.articleNo,
    }),
    [discount.articleNo, discount.code, discount.discount, discount.partnerId]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<InferType<typeof adminDiscountSchema>>({
    mode: 'all',
    resolver: yupResolver(adminDiscountSchema),
    defaultValues: defaultDiscountCodeValues,
  })

  const partnerOptions = Object.values(ManagementPartner).map((partner) => ({
    value: partner,
    label: partner,
  }))

  React.useEffect(() => {
    reset(defaultDiscountCodeValues)
    setShowDeleteConfirmation(false)
  }, [defaultDiscountCodeValues, reset])

  const closeEditDiscountCodeModal = (): void => {
    close()
    setShowDeleteConfirmation(false)
    reset(defaultDiscountCodeValues)
  }

  const { mutate: mutatePatchDiscountCode, isPending: isPatchPending } =
    useMutation({
      mutationFn: patchDiscountCodeRequest,
      onSuccess: async () => {
        setToastNotification(ToastType.SUCCESS, 'Discount code updated')
        closeEditDiscountCodeModal()
        await queryClient.invalidateQueries({
          queryKey: adminQueries.getDiscountCodes().queryKey,
        })
      },
      onError: () => {
        setToastNotification(ToastType.DANGER, 'Could not update discount code')
      },
    })

  const { mutate: mutateRemoveDiscountCode, isPending: isRemovePending } =
    useMutation({
      mutationFn: removeDiscountCodeRequest,
      onSuccess: async () => {
        setToastNotification(ToastType.SUCCESS, 'Discount code removed')
        closeEditDiscountCodeModal()
        await queryClient.invalidateQueries({
          queryKey: adminQueries.getDiscountCodes().queryKey,
        })
      },
      onError: (error) => {
        const axiosError = error as AxiosError<{ err?: string }>
        const isUsageError =
          axiosError.response?.status === 400 &&
          axiosError.response?.data?.err === 'discount code used by societies'

        setToastNotification(
          ToastType.DANGER,
          isUsageError
            ? 'Kan inte ta bort. Rabattkoden anvands av minst en forening.'
            : 'Could not remove discount code'
        )
      },
    })

  const onEditSubmit = (data: InferType<typeof adminDiscountSchema>): void => {
    mutatePatchDiscountCode({
      code: discount.code,
      body: {
        code: data.code,
        discount: Number(data.discount),
        partnerId: getPartnerId(data.partnerNameId as ManagementPartner),
        articleNo: data.articleNo,
      },
    })
  }

  return (
    <Modal show={show} size="lg">
      {{
        header: (
          <ModalHeader onClose={closeEditDiscountCodeModal}>
            Redigera rabattkod
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            <form className="space-y-4" onSubmit={handleSubmit(onEditSubmit)}>
              <Controller
                control={control}
                name="code"
                render={({ field: { value, name, onChange, onBlur } }) => (
                  <FormControl
                    label="Kod"
                    name={name}
                    error={errors.code && getErrorMessage(errors.code)}
                  >
                    <TextInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.code}
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="discount"
                render={({ field: { value, name, onChange, onBlur } }) => (
                  <FormControl
                    label="Rabatt (%)"
                    name={name}
                    error={errors.discount && getErrorMessage(errors.discount)}
                  >
                    <TextInput
                      type="number"
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.discount}
                      disableOnWheel
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="partnerNameId"
                render={({ field: { value, name, onChange } }) => (
                  <FormControl
                    label="Partner"
                    name={name}
                    error={
                      errors.partnerNameId &&
                      getErrorMessage(errors.partnerNameId)
                    }
                  >
                    <SelectDropdown
                      options={partnerOptions}
                      value={partnerOptions.find(
                        (partner) => partner.value === value
                      )}
                      onChange={(option) => onChange(option?.value)}
                      error={!!errors.partnerNameId}
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="articleNo"
                render={({ field: { value, name, onChange, onBlur } }) => (
                  <FormControl
                    label="Article no"
                    name={name}
                    error={
                      errors.articleNo && getErrorMessage(errors.articleNo)
                    }
                  >
                    <TextInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.articleNo}
                    />
                  </FormControl>
                )}
              />

              {showDeleteConfirmation && (
                <div className="rounded border border-red p-3 text-left">
                  <p style={theme.textVariants.base}>
                    Vill du ta bort rabattkoden {discount.code}?
                  </p>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      variant={ButtonVariant.TEXT}
                      label="Avbryt"
                      onClick={() => setShowDeleteConfirmation(false)}
                    />
                    <Button
                      variant={ButtonVariant.DANGER}
                      label="Bekräfta"
                      loading={isRemovePending}
                      disabled={isRemovePending || isPatchPending}
                      onClick={() =>
                        mutateRemoveDiscountCode({
                          code: discount.code,
                        })
                      }
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button
                  variant={ButtonVariant.DANGER}
                  label={
                    showDeleteConfirmation ? 'Vantar pa bekräftelse' : 'Ta bort'
                  }
                  disabled={
                    showDeleteConfirmation ||
                    isRemovePending ||
                    isPatchPending ||
                    discount.societyCount > 0
                  }
                  onClick={() => setShowDeleteConfirmation(true)}
                />
                {discount.societyCount > 0 && (
                  <p style={theme.textVariants.base}>
                    Kan inte ta bort. Anvands av {discount.societyCount}{' '}
                    föreningar.
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    variant={ButtonVariant.PRIMARY}
                    disabled={!isValid || isPatchPending || isRemovePending}
                    loading={isPatchPending}
                    label="Spara"
                  />
                </div>
              </div>
            </form>
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
